import { Inject, Injectable, Optional } from '@angular/core';
import { distinctUntilChanged, map, Observable } from 'rxjs';
import { CoachingVideoApi } from '../api/coaching-video.api';
import { ICoachingVideoApi } from '../api/interfaces/coaching-video-abstract.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import {
  AbstractPaginableFacade,
  ItemsPaginableState,
} from './abstract-paginable.facade';
import { Coaching } from '@kinomap-dev/ng-ui-lib';
import { ToasterService } from 'app/services/toaster.service';

// 3 Minutes
const CACHE_VALIDITY = 180000;

@Injectable({
  providedIn: 'root',
})
export class CoachingVideosFacade extends AbstractPaginableFacade<
  Coaching,
  ItemsPaginableState<Coaching>
> {
  public readonly coachingVideos$: Observable<Coaching[]>;
  public readonly total$: Observable<number>;
  public readonly loading$: Observable<boolean>;

  constructor(
    private readonly coachingVideoApi: ICoachingVideoApi,
    @Inject(NEVER_TOKEN) @Optional() private userId: number,
    private toasterService: ToasterService
  ) {
    super(
      {
        loading: false,
        total: null,
        items: [],
        lastRequestedIndex: 0,
        lastLoadingTime: 0,
      },
      CACHE_VALIDITY
    );
    this.coachingVideos$ = this.state$.pipe(
      map((state) => state.items),
      distinctUntilChanged()
    );
    this.total$ = this.state$.pipe(
      map((state) => state.total),
      distinctUntilChanged()
    );
    this.loading$ = this.state$.pipe(
      map((state) => state.loading),
      distinctUntilChanged()
    );
  }

  loadFromApi(start: number, end: number) {
    // Do not reload if a page is currently loading
    if (end <= 0 || this.isTotallyLoaded() || this.state.loading) {
      return;
    }

    const { page, pageSize } = this.computePagesAndSizeFromInterval(start, end);
    const offset = (page - 1) * pageSize;

    this.setState({
      loading: true,
      lastRequestedIndex: end,
    });

    this.coachingVideoApi
      .getCoachingVideos(page, pageSize, this.userId)
      .subscribe({
        next: ({ data: coachingInformation, meta }) => {
          const allCoachingVideos = this.mergeNewItemsToOldOnes(
            coachingInformation,
            offset
          );
          this.cacheValidityTime = Date.now() + this.cacheValidity;
          this.setState({
            loading: false,
            items: allCoachingVideos,
            total: meta.total,
          });
          // TODO => Run following data in queue
        },
        error: (err) => {
          console.error(err);
          this.setState({
            loading: false,
          });
        },
      });
  }

  deleteCoachingVideo(coachingVideoId: number) {
    (this.coachingVideoApi as CoachingVideoApi)
      .deleteCoachingVideo(coachingVideoId)
      .subscribe({
        error: () =>
          this.toasterService.warning(
            null,
            `Unable to remove coaching video with id '${coachingVideoId}'`
          ),
        next: () => {
          this.toasterService.success(null, 'Your coaching video has been deleted.');
          const coachingVideoIndex: number = this.state.items.findIndex(
            (coachingVideo: Coaching) => coachingVideo.id === coachingVideoId
          );
          if (coachingVideoIndex === -1) {
            return;
          }
          const newCoachingVideos: Coaching[] = [...this.state.items];
          newCoachingVideos.splice(coachingVideoIndex, 1);
          this.setState({
            items: newCoachingVideos,
            total: this.state.total - 1,
            lastRequestedIndex: this.state.lastRequestedIndex - 1,
          });
        },
      });
  }
}
